/**
 * "Set up with AI" (spec §8 P-8.1 to P-8.3), the parts with no screen in them. PURE: no Vue, no
 * store, no I/O.
 *
 * ── WHAT THE BROWSER SENDS BACK IS ONLY A CHOICE ─────────────────────────────────────────
 * A first draft is saved by the server from what is stored (F26). This file turns the owner's
 * Keep, Edit and Remove into `{ keep, edits }` and her ticks into `{ pick }`, and nothing else:
 * never a composed Personality, never a line the draft did not have.
 */
import { problemMessage } from './task-flow-draft.js';

/**
 * A problem the task draft came back with (spec P-8.2), as a sentence. The kinds only a described
 * draft has are said here; every other kind is the composer's own (`problemMessage`).
 */
const OUTLINE_PROBLEMS = Object.freeze({
  no_steps: 'WASLO.SETUP_AI.DESCRIBE.PROBLEMS.NO_STEPS',
  steps_many: 'WASLO.SETUP_AI.DESCRIBE.PROBLEMS.STEPS_MANY',
  options_many: 'WASLO.SETUP_AI.DESCRIBE.PROBLEMS.OPTIONS_MANY',
  no_options: 'WASLO.SETUP_AI.DESCRIBE.PROBLEMS.NO_OPTIONS',
  option_label_long: 'WASLO.SETUP_AI.DESCRIBE.PROBLEMS.OPTION_LABEL_LONG',
  question_long: 'WASLO.SETUP_AI.DESCRIBE.PROBLEMS.QUESTION_LONG',
  question_missing: 'WASLO.SETUP_AI.DESCRIBE.PROBLEMS.QUESTION_MISSING',
  message_long: 'WASLO.SETUP_AI.DESCRIBE.PROBLEMS.MESSAGE_LONG',
  shape: 'WASLO.SETUP_AI.DESCRIBE.PROBLEMS.SHAPE',
});
export const describedProblemMessage = (problem, t) =>
  Object.prototype.hasOwnProperty.call(OUTLINE_PROBLEMS, problem?.problem)
    ? t(OUTLINE_PROBLEMS[problem.problem])
    : problemMessage(problem, t);

/** The three ways a drafted line can be decided on the review screen. */
export const LINE_DECISIONS = Object.freeze(['keep', 'edit', 'remove']);

/** What the screen says each source is (`WASLO.SETUP_AI.SOURCE.*`). */
export const sourceKey = source => {
  switch (source?.kind) {
    case 'profile':
      return 'WASLO.SETUP_AI.SOURCE.PROFILE';
    case 'website':
      return 'WASLO.SETUP_AI.SOURCE.WEBSITE';
    case 'account_name':
      return 'WASLO.SETUP_AI.SOURCE.ACCOUNT_NAME';
    default:
      return 'WASLO.SETUP_AI.SOURCE.WASLO';
  }
};

/** Every line and answer starts kept, as drafted; every question starts unanswered. */
export const initialDecisions = draft => {
  const decisions = {};
  [...(draft?.lines ?? []), ...(draft?.answers ?? [])].forEach(item => {
    decisions[item.id] = { decision: 'keep', text: null };
  });
  return decisions;
};

/**
 * The apply body for a first draft. A line marked `edit` with the same words as drafted is a
 * keep; an edit to nothing is a remove (an empty line is not a line).
 *
 * @param {object} draft  the BFF's `FirstDraftDTO`
 * @param {Record<string, { decision: string, text: string|null, question?: string, answer?: string }>} decisions
 * @param {Record<string, string>} answered  the owner's words per question id
 */
export const keepBodyOf = (draft, decisions, answered = {}) => {
  const keep = [];
  const lines = {};
  const answers = {};
  (draft?.lines ?? []).forEach(line => {
    const d = decisions[line.id];
    if (!d || d.decision === 'remove') return;
    const text =
      d.decision === 'edit' ? String(d.text ?? '').trim() : line.text;
    if (text === '') return;
    keep.push(line.id);
    if (text !== line.text) lines[line.id] = text;
  });
  (draft?.answers ?? []).forEach(item => {
    const d = decisions[item.id];
    if (!d || d.decision === 'remove') return;
    const question = String(d.question ?? item.question).trim();
    const answer = String(d.answer ?? item.answer).trim();
    if (question === '' || answer === '') return;
    keep.push(item.id);
    if (question !== item.question || answer !== item.answer) {
      answers[item.id] = { question, answer };
    }
  });
  const questions = {};
  (draft?.questions ?? []).forEach(q => {
    const said = String(answered[q.id] ?? '').trim();
    if (said !== '') questions[q.id] = said;
  });
  const edits = {};
  if (Object.keys(lines).length) edits.lines = lines;
  if (Object.keys(answers).length) edits.answers = answers;
  if (Object.keys(questions).length) edits.questions = questions;
  return { keep, edits };
};

/**
 * How many answers a save adds: kept answers, and every question answered except a business one
 * (that becomes a line). When it is more than none, Save these costs one `teach` (spec P-5.12).
 */
export const answersAddedBy = (draft, body) => {
  const kept = body.keep.filter(id => id.startsWith('answer.')).length;
  const answered = Object.keys(body.edits.questions ?? {}).filter(id => {
    const q = (draft?.questions ?? []).find(item => item.id === id);
    return q && q.target !== 'business';
  }).length;
  return kept + answered;
};

/** Pages in the order a change set is reviewed, with the words each is called. */
export const CHANGE_PAGES = Object.freeze([
  'answers',
  'personality',
  'tasks',
  'menus',
  'hours',
  'notices',
]);

/** A change set grouped by page, in `CHANGE_PAGES` order, each group in the proposal's order. */
export const changeGroups = items =>
  CHANGE_PAGES.map(page => ({
    page,
    items: (items ?? []).filter(item => item.page === page),
  })).filter(group => group.items.length > 0);

/** An apply that adds or re-words an answer's question needs one confirmed `teach`. */
export const pickEmbeds = (items, pick) =>
  (items ?? []).some(
    item =>
      pick.includes(item.index) &&
      (item.action === 'answer.add' ||
        (item.action === 'answer.edit' && item.question !== ''))
  );

/** Where a change lives, as a path under the account. */
export const changePagePath = (accountId, item) => {
  const base = `accounts/${accountId}/waslo`;
  const task = /^(?:option|task):([0-9]+)/.exec(item?.targetId ?? '');
  const menu = /^row:([0-9]+)/.exec(item?.targetId ?? '');
  switch (item?.page) {
    case 'personality':
    case 'hours':
      return `${base}/personality`;
    case 'tasks':
      return task ? `${base}/tasks/${task[1]}` : `${base}/tasks`;
    case 'menus':
      return menu ? `${base}/menus/${menu[1]}` : `${base}/menus`;
    default:
      return `${base}/answers`;
  }
};

/**
 * The account's channels in the words the sidecar takes for a task description (spec P-8.2, F25:
 * `website`, never `web`). A channel it has no word for is `other`, once.
 */
const DESCRIBE_CHANNELS = [
  'whatsapp',
  'instagram',
  'messenger',
  'telegram',
  'website',
];
export const describeChannels = channels => {
  const list = Array.isArray(channels) ? channels : [];
  const known = DESCRIBE_CHANNELS.filter(kind => list.includes(kind));
  const other = list.some(kind => !DESCRIBE_CHANNELS.includes(kind));
  return other ? [...known, 'other'] : known;
};

/** A recording is two minutes at most (spec §8 "Voice input"). */
export const VOICE_MAX_SECONDS = 120;

const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

/**
 * Opening hours as the owner reads them, from the canonical JSON a change carries
 * (`{"sun":["08:00","20:00"],"fri":null}`). `dayName` names a day key; a closed day says so with
 * `closed`. Text that is not hours comes back as it is.
 */
export const hoursLine = (text, dayName, closed) => {
  let hours;
  try {
    hours = JSON.parse(text);
  } catch {
    return text;
  }
  if (!hours || typeof hours !== 'object' || Array.isArray(hours)) return text;
  return DAYS.filter(day => day in hours)
    .map(day => {
      const range = hours[day];
      return Array.isArray(range)
        ? `${dayName(day)} ${range[0]} ${'\u2192'} ${range[1]}`
        : `${dayName(day)} ${closed}`;
    })
    .join(', ');
};
